import { useEffect, useState } from 'react';
import { isRestrictedRegion } from '../utils/restrictedRegion';
import { getUserRegion } from '../services/region';

/**
 * CORE-730 — shared region state for the social-auth gate.
 *
 * Both SocialAuthButtons variants (web + native) used to keep their own copy
 * of this state + effect, which is how the RU gate drifted between surfaces.
 * They now share this one hook, and feed its output into `shouldShowSocialAuth`
 * (see `utils/socialAuthGate.ts`).
 *
 * Mechanism is unchanged: seed synchronously from the cached region / timezone
 * heuristic, then confirm against the authoritative `/api/region` GeoIP answer.
 * `regionResolved` flips once that lookup settles (success, null, or error) so
 * callers can keep the row hidden until the region is actually known.
 */
export function useSocialAuthRegion(): { restricted: boolean; regionResolved: boolean } {
  const [restricted, setRestricted] = useState<boolean>(() => isRestrictedRegion(null));
  const [regionResolved, setRegionResolved] = useState<boolean>(false);

  useEffect(() => {
    let alive = true;
    getUserRegion()
      .then((country) => {
        if (!alive) return;
        // A definite country code is authoritative; null → keep the heuristic.
        setRestricted(country ? country === 'RU' : isRestrictedRegion(null));
      })
      .catch(() => { /* keep the heuristic seed */ })
      .finally(() => { if (alive) setRegionResolved(true); });
    return () => { alive = false; };
  }, []);

  return { restricted, regionResolved };
}
